import { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getFunctionErrorMessage } from '@/lib/functionError';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgName?: string;
  used?: number;
  max?: number;
  defaultPhone?: string;
}

export function RequestEnterpriseLicensesDialog({
  open, onOpenChange, orgName, used = 0, max = 0, defaultPhone = '',
}: Props) {
  const [licenses, setLicenses] = useState('5');
  const [phone, setPhone] = useState(defaultPhone);
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    const qty = parseInt(licenses, 10);
    if (!qty || qty < 1) {
      toast({ title: 'Indica cuántas licencias necesitas', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('request-enterprise-licenses', {
        body: { requested_licenses: qty, phone, notes },
      });
      const inner = (data as { error?: string } | null)?.error;
      if (error || inner) {
        toast({
          title: 'No se pudo enviar la solicitud',
          description: inner || (await getFunctionErrorMessage(error)),
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Solicitud enviada',
        description: 'Recibimos tu solicitud, el equipo comercial te contactará.',
      });
      setNotes('');
      onOpenChange(false);
    } catch (e) {
      toast({
        title: 'No se pudo enviar la solicitud',
        description: await getFunctionErrorMessage(e),
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Contratar licencias Enterprise</DialogTitle>
          <DialogDescription>
            {orgName ? `${orgName} · ` : ''}
            {used}/{max} licencias en uso. Cuéntanos qué necesitas y el equipo comercial te
            contactará.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="req-licenses">¿Cuántas licencias necesitas?</Label>
            <Input
              id="req-licenses"
              type="number"
              min={1}
              max={1000}
              value={licenses}
              onChange={(e) => setLicenses(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="req-phone">Teléfono de contacto</Label>
            <Input
              id="req-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+57 300 000 0000"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="req-notes">Comentario (opcional)</Label>
            <Textarea
              id="req-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Contexto, fechas estimadas, áreas que usarán las licencias…"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancelar
          </Button>
          <Button className="gap-2" onClick={handleSubmit} disabled={sending}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Enviar solicitud
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RequestEnterpriseLicensesDialog;
